import React from "react";
import {
	Box,
	Chip,
	Tab,
	Tabs,
	Typography,
} from "@mui/material";
import StorefrontTwoToneIcon from "@mui/icons-material/StorefrontTwoTone";
import MiscellaneousServicesTwoToneIcon from "@mui/icons-material/MiscellaneousServicesTwoTone";
import QrCodeScannerTwoToneIcon from "@mui/icons-material/QrCodeScannerTwoTone";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface Props {
	activeTab: string;
	onTabChange: (event: React.SyntheticEvent, newTab: string) => void;
	stallCount: number;
	serviceCount: number;
	scanCount: number;
}

const ExhibitorTabsHeader: React.FC<Props> = ({
	activeTab,
	onTabChange,
	stallCount,
	serviceCount,
	scanCount,
}) => {
	return (
		<Box
			sx={{
				p: 2,
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				flexWrap: "wrap",
				gap: 2,
				borderBottom: "1px solid",
				borderColor: "divider",
				bgcolor: "#fcfcfd",
			}}
		>
			{/* Segmented Tab Container */}
			<Box
				sx={{
					p: 0.5,
					bgcolor: "#eef2f6",
					borderRadius: 2.5,
					display: "inline-flex",
					border: "1px solid #e2e8f0",
				}}
			>
				<Tabs
					value={activeTab}
					onChange={onTabChange}
					TabIndicatorProps={{
						style: { display: "none" },
					}}
					sx={{
						minHeight: 38,
						"& .MuiTabs-flexContainer": {
							gap: 0,
						},
						"& .MuiTab-root": {
							minHeight: 38,
							textTransform: "none",
							borderRadius: 2,
							px: 2.5,
							py: 0.8,
							color: "#475569",
							fontWeight: 600,
							fontSize: "0.92rem",
							transition: "all 0.2s ease-in-out",
							"&:hover": {
								color: "primary.main",
								bgcolor: "rgba(255, 255, 255, 0.6)",
							},
							"&.Mui-selected": {
								bgcolor: "primary.main",
								color: "#ffffff !important",
								fontWeight: 700,
								boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
							},
						},
					}}
				>
					<Tab
						value="stalls"
						icon={
							<StorefrontTwoToneIcon
								fontSize="small"
								sx={{
									color: activeTab === "stalls" ? "#ffffff" : "inherit",
								}}
							/>
						}
						iconPosition="start"
						label={
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<span>Stalls</span>
								<Chip
									label={stallCount}
									size="small"
									sx={{
										height: 20,
										fontSize: "0.72rem",
										fontWeight: 700,
										borderRadius: "10px",
										bgcolor:
											activeTab === "stalls"
												? "rgba(255, 255, 255, 0.25)"
												: "grey.300",
										color:
											activeTab === "stalls"
												? "#ffffff"
												: "text.primary",
									}}
								/>
							</Box>
						}
					/>
					<Tab
						value="services"
						icon={
							<MiscellaneousServicesTwoToneIcon
								fontSize="small"
								sx={{
									color: activeTab === "services" ? "#ffffff" : "inherit",
								}}
							/>
						}
						iconPosition="start"
						label={
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<span>Services</span>
								<Chip
									label={serviceCount}
									size="small"
									sx={{
										height: 20,
										fontSize: "0.72rem",
										fontWeight: 700,
										borderRadius: "10px",
										bgcolor:
											activeTab === "services"
												? "rgba(255, 255, 255, 0.25)"
												: "grey.300",
										color:
											activeTab === "services"
												? "#ffffff"
												: "text.primary",
									}}
								/>
							</Box>
						}
					/>
					<Tab
						value="scanned_visitors"
						icon={
							<QrCodeScannerTwoToneIcon
								fontSize="small"
								sx={{
									color:
										activeTab === "scanned_visitors"
											? "#ffffff"
											: "inherit",
								}}
							/>
						}
						iconPosition="start"
						label={
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<span>Scanned Visitors</span>
								<Chip
									label={scanCount}
									size="small"
									sx={{
										height: 20,
										fontSize: "0.72rem",
										fontWeight: 700,
										borderRadius: "10px",
										bgcolor:
											activeTab === "scanned_visitors"
												? "rgba(255, 255, 255, 0.25)"
												: "grey.300",
										color:
											activeTab === "scanned_visitors"
												? "#ffffff"
												: "text.primary",
									}}
								/>
							</Box>
						}
					/>
				</Tabs>
			</Box>

			<Typography
				variant="body2"
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 0.5,
					fontWeight: 600,
					color: "text.secondary",
				}}
			>
				<InfoOutlinedIcon fontSize="small" color="action" />
				{activeTab === "stalls"
					? `Total ${stallCount} stalls allocated`
					: activeTab === "services"
					? `Total ${serviceCount} services available`
					: `Total ${scanCount} visitor scans recorded`}
			</Typography>
		</Box>
	);
};

export default ExhibitorTabsHeader;
